import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Loading, Avatar } from "../components/common";
import { toast } from "sonner";
import { Target, Shuffle, ArrowUp, ArrowDown, Check, Globe, Diamond } from "lucide-react";

const FORMATS = [1, 3, 5, 7, 9, 11];

export default function NewMatch() {
  const [players, setPlayers] = useState(null);
  const [gameType, setGameType] = useState("501");
  const [selected, setSelected] = useState([]); // ordered ids
  const [bestOf, setBestOf] = useState(5);
  const [custom, setCustom] = useState(false);
  const [customVal, setCustomVal] = useState("");
  const [finishRule, setFinishRule] = useState("double_out");
  const [startRule, setStartRule] = useState("straight_in");
  const [starting, setStarting] = useState(false);
  const [logMode, setLogMode] = useState(false);
  const [winnerId, setWinnerId] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (winnerId && !selected.includes(winnerId)) setWinnerId("");
  }, [selected, winnerId]);

  useEffect(() => {
    (async () => setPlayers((await api.get("/players")).data))();
  }, []);

  if (players === null) return <Loading label="Loading setup" />;

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= selected.length) return;
    const copy = [...selected];
    [copy[idx], copy[ni]] = [copy[ni], copy[idx]];
    setSelected(copy);
  };

  const randomize = () => setSelected((s) => [...s].sort(() => Math.random() - 0.5));

  const legsNeeded = Math.floor((bestOf) / 2) + 1;

  const start = async () => {
    if (selected.length < 2) return toast.error("Select at least 2 players");
    let bo = bestOf;
    if (custom) {
      bo = parseInt(customVal, 10);
      if (!bo || bo < 1 || bo % 2 === 0) return toast.error("Custom must be an odd number");
    }
    setStarting(true);
    try {
      const { data } = await api.post("/matches", {
        game_type: gameType,
        finish_rule: finishRule,
        start_rule: startRule,
        best_of: bo,
        player_ids: selected,
      });
      navigate(`/match/${data.id}`);
    } catch (e) {
      toast.error("Could not start match");
      setStarting(false);
    }
  };

  const nameOf = (id) => players.find((p) => p.id === id)?.name || "?";

  const logResult = async () => {
    if (selected.length < 2) return toast.error("Select at least 2 players");
    if (!winnerId || !selected.includes(winnerId)) return toast.error("Pick the winner");
    setStarting(true);
    try {
      await api.post("/matches/manual", {
        game_type: gameType,
        player_ids: selected,
        winner_id: winnerId,
      });
      toast.success(`Result logged — ${nameOf(winnerId)} +1 win`);
      navigate(`/players/${winnerId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log result");
      setStarting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">
          Match Setup
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">
          {logMode ? "Log Past Result" : `New ${gameType === "501" ? "501" : gameType === "baseball" ? "Baseball" : "ATW"} Match`}
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-1">
        <button
          data-testid="mode-play-btn"
          onClick={() => {
            setLogMode(false);
            setWinnerId("");
          }}
          className={`py-2.5 rounded-sm font-bold uppercase text-xs tracking-wide transition-colors ${
            !logMode ? "bg-[#FF3B30] text-white" : "panel text-slate-400 hover:text-white"
          }`}
        >
          Play Live
        </button>
        <button
          data-testid="mode-log-btn"
          onClick={() => {
            setLogMode(true);
            setWinnerId("");
          }}
          className={`py-2.5 rounded-sm font-bold uppercase text-xs tracking-wide transition-colors ${
            logMode ? "bg-[#FF3B30] text-white" : "panel text-slate-400 hover:text-white"
          }`}
        >
          Log Past Result
        </button>
      </div>
      {logMode && (
        <p className="text-sm text-slate-400 -mt-4">
          Already played a game (or one didn't save)? Pick the game, the players, and the winner to add
          it to the records — <span className="text-white font-semibold">+1 win to the winner, a loss to
          everyone else</span>. Nothing else is reset.
        </p>
      )}

      <Section title="Game">
        <div className="grid grid-cols-3 gap-1">
          <Choice active={gameType === "501"} onClick={() => setGameType("501")} testid="game-501">
            <Target className="w-5 h-5" /> 501
          </Choice>
          <Choice
            active={gameType === "around_the_world"}
            onClick={() => setGameType("around_the_world")}
            testid="game-atw"
          >
            <Globe className="w-5 h-5" /> Around the World
          </Choice>
          <Choice
            active={gameType === "baseball"}
            onClick={() => setGameType("baseball")}
            testid="game-baseball"
          >
            <Diamond className="w-5 h-5" /> Baseball
          </Choice>
        </div>
      </Section>

      <Section title="Select Players">
        {players.length < 2 ? (
          <p className="text-slate-500">
            Add at least 2 players first on the{" "}
            <button onClick={() => navigate("/players")} className="text-[#007AFF] underline">
              Players
            </button>{" "}
            page.
          </p>
        ) : (
          <div className="grid gap-1 sm:grid-cols-2">
            {players.map((p) => {
              const active = selected.includes(p.id);
              return (
                <button
                  key={p.id}
                  data-testid={`select-player-${p.id}`}
                  onClick={() => toggle(p.id)}
                  className={`panel rounded-sm p-3 flex items-center gap-3 text-left transition-colors duration-150 ${
                    active ? "border-[#007AFF] bg-[#0d1a2b]" : "hover:border-slate-600"
                  }`}
                >
                  <Avatar name={p.name} size={40} />
                  <span className="font-display text-lg font-bold text-white flex-1">
                    {p.name}
                  </span>
                  {active && (
                    <span className="w-6 h-6 flex items-center justify-center bg-[#007AFF] rounded-sm">
                      <Check className="w-4 h-4 text-white" strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Section>

      {gameType === "baseball" && !logMode && (
        <Section title="Format">
          <div className="panel rounded-sm p-4 text-sm text-slate-300">
            <span className="text-[#FFCC00] font-bold">9 innings</span> (targets 1–9). Each player throws
            3 darts per inning at that inning's number. Single = 1 run, Double = 2, Triple = 3. Most total
            runs after 9 innings wins — ties go to extra innings.
          </div>
        </Section>
      )}

      {!logMode && (
      <Section title="Match Format">        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1">
          {FORMATS.map((f) => (
            <Choice
              key={f}
              active={!custom && bestOf === f}
              onClick={() => {
                setCustom(false);
                setBestOf(f);
              }}
              testid={`format-bo${f}`}
            >
              Best of {f}
            </Choice>
          ))}
          <Choice active={custom} onClick={() => setCustom(true)} testid="format-custom">
            Custom
          </Choice>
        </div>
        {custom && (
          <input
            data-testid="custom-bestof-input"
            value={customVal}
            onChange={(e) => setCustomVal(e.target.value)}
            placeholder="Odd number (e.g. 13)"
            type="number"
            className="mt-2 w-full max-w-xs bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
          />
        )}
        <p className="text-sm text-slate-400 mt-3">
          First to <span className="text-[#FFCC00] font-bold">{legsNeeded}</span>{" "}
          {gameType === "501" ? "legs" : gameType === "baseball" ? "games" : "rounds"} wins the match.
        </p>
      </Section>
      )}

      {gameType === "501" && !logMode && (
        <Section title="Starting Rule">
          <div className="grid grid-cols-2 gap-1">
            <Choice
              active={startRule === "straight_in"}
              onClick={() => setStartRule("straight_in")}
              testid="start-straight"
            >
              Straight In
            </Choice>
            <Choice
              active={startRule === "double_in"}
              onClick={() => setStartRule("double_in")}
              testid="start-double"
            >
              Double In
            </Choice>
          </div>
        </Section>
      )}

      {gameType === "501" && !logMode && (
        <Section title="Finish Rule">
          <div className="grid grid-cols-2 gap-1">
            <Choice
              active={finishRule === "double_out"}
              onClick={() => setFinishRule("double_out")}
              testid="finish-double"
            >
              Double Out
            </Choice>
            <Choice
              active={finishRule === "straight_out"}
              onClick={() => setFinishRule("straight_out")}
              testid="finish-straight"
            >
              Straight Out
            </Choice>
          </div>
        </Section>
      )}

      {selected.length >= 2 && !logMode && (
        <Section
          title="Throwing Order"
          action={
            <button
              data-testid="randomize-order-btn"
              onClick={randomize}
              className="flex items-center gap-1.5 text-sm font-bold uppercase text-[#007AFF] hover:text-[#3395FF]"
            >
              <Shuffle className="w-4 h-4" /> Randomize
            </button>
          }
        >
          <div className="space-y-1">
            {selected.map((id, idx) => (
              <div
                key={id}
                className="panel rounded-sm p-3 flex items-center gap-3"
                data-testid={`order-row-${id}`}
              >
                <span className="font-display text-2xl font-black text-[#FF3B30] w-6">
                  {idx + 1}
                </span>
                <Avatar name={nameOf(id)} size={36} />
                <span className="font-display text-lg font-bold text-white flex-1">
                  {nameOf(id)}
                </span>
                <button onClick={() => move(idx, -1)} className="text-slate-400 hover:text-white p-1">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button onClick={() => move(idx, 1)} className="text-slate-400 hover:text-white p-1">
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </Section>
      )}

      {logMode && selected.length >= 2 && (
        <Section title="Winner">
          <div className="grid grid-cols-2 gap-1">
            {selected.map((id) => (
              <button
                key={id}
                data-testid={`winner-${id}`}
                onClick={() => setWinnerId(id)}
                className={`flex items-center gap-2 p-3 rounded-sm transition-colors ${
                  winnerId === id ? "bg-[#FFCC00] text-black" : "panel text-slate-300 hover:text-white"
                }`}
              >
                <Avatar name={nameOf(id)} size={28} />
                <span className="font-bold uppercase text-sm">{nameOf(id)}</span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {logMode ? (
        <button
          data-testid="log-result-btn"
          onClick={logResult}
          disabled={starting || selected.length < 2 || !winnerId}
          className="w-full bg-[#FFCC00] hover:bg-[#e6b800] disabled:opacity-40 text-black font-display text-2xl font-black uppercase tracking-wider py-5 rounded-sm transition-colors duration-150"
        >
          {starting ? "Logging..." : "Log Result"}
        </button>
      ) : (
        <button
          data-testid="start-match-btn"
          onClick={start}
          disabled={starting || selected.length < 2}
          className="w-full bg-[#FF3B30] hover:bg-[#ff5148] disabled:opacity-40 text-white font-display text-2xl font-black uppercase tracking-wider py-5 rounded-sm transition-colors duration-150"
        >
          {starting ? "Starting..." : "Start Match"}
        </button>
      )}
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function Choice({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-sm font-bold uppercase tracking-wide text-sm transition-colors duration-150 border ${
        active
          ? "bg-[#007AFF] border-[#007AFF] text-white"
          : "panel text-slate-300 hover:border-slate-600"
      }`}
    >
      {children}
    </button>
  );
}
